import React, { useState, useEffect } from 'react';
import { Settings, Save, RotateCcw, Info, Shield, Bell, Package, Lock } from 'lucide-react';
import api from '../api/axios';
import { Card } from './shared/Card';
import { Button } from './shared/Button';
import { toast } from 'react-toastify';
import './SystemConfig.css';
import { plural } from "../utils/texto";

/**
 * CONFIGURACIÓN DEL SISTEMA.
 *
 * Esta pantalla la usa la administración de cada residencial —juntas
 * directivas y ADESCOs—, no gente técnica. De ahí las tres reglas que
 * gobiernan todo lo de abajo:
 *
 *   1. Lo que se lee primero está en español. El nombre técnico se conserva
 *      a la vista, en pequeño, porque soporte lo necesita para dar
 *      instrucciones por teléfono.
 *   2. Los controles son controles de verdad (`<button role="switch">` y
 *      `<input type="checkbox">`), operables con el teclado.
 *   3. Nada se guarda solo: los cambios se acumulan y se mandan con
 *      «Guardar cambios».
 */

/**
 * Los nombres de grupo en español.
 *
 * Las categorías que el servidor devuelve son exactamente estas cuatro
 * (`controllers/systemConfigController.js`). Si apareciera una nueva, se
 * enseña su nombre tal cual en vez de dejar el hueco vacío.
 */
const NOMBRES_GRUPO = {
  general: 'General',
  notifications: 'Notificaciones',
  security: 'Seguridad',
  features: 'Funciones',
};

const ICONOS_GRUPO = {
  general: { icono: Settings, color: 'var(--primary)' },
  notifications: { icono: Bell, color: 'var(--warning)' },
  security: { icono: Shield, color: 'var(--danger)' },
  features: { icono: Package, color: 'var(--success)' },
};

/**
 * Grupos ocultos TEMPORALMENTE.
 *
 * «Notificaciones» (`sms_enabled` y `sms_provider`) se oculta a solicitud de
 * Rafael Martínez para una demo. No se elimina el código: sólo se oculta
 * temporalmente. Para volver a mostrarla, quite 'notifications' de esta lista.
 *
 * Mientras esté oculta, `sms_enabled` queda en `false` —de fábrica en
 * `backend/models/SystemConfig.js` y en cada residencial—, así que no se envían
 * mensajes y nadie necesita el interruptor.
 */
const GRUPOS_OCULTOS = ['notifications'];

/**
 * Ajustes que esta pantalla no deja editar, con el motivo a la vista.
 *
 * `sms_provider` es el único: el servidor sólo tiene Twilio integrado
 * (`backend/services/smsService.js` lo importa directamente), así que escribir
 * otro proveedor apagaría los SMS sin que nadie se entere.
 *
 * `qr_valid_same_day_only` estaba también aquí y NO corresponde: es un ajuste
 * ordinario que la administración debe poder cambiar, y el servidor no lo
 * impide. Bloquearlo tenía además un efecto peor que el bloqueo: se pintaba
 * con medio `opacity`, así que un interruptor encendido se veía verde pálido
 * y no había forma de distinguirlo de uno apagado a medias.
 */
const SOLO_LECTURA = {
  sms_provider: 'Twilio es el único proveedor de SMS que el sistema tiene integrado.',
};

/**
 * Las duraciones de pase, copiadas de `backend/utils/catalogos.js`
 * (`TIPOS_PASE`) con sus valores y etiquetas exactos.
 *
 * No se piden a `GET /api/catalogos` a propósito: ese endpoint ya devuelve
 * `tiposPase` FILTRADO por `allowed_pass_types`, que es justo el ajuste que
 * aquí se edita. Usándolo, una duración apagada desaparecería de la pantalla
 * y no habría manera de volver a encenderla.
 *
 * Si la lista del servidor cambia, hay que cambiarla aquí también.
 */
const TIPOS_PASE = [
  { valor: 'unica', etiqueta: 'Una sola entrada' },
  { valor: 'hoy', etiqueta: 'Sólo hoy' },
  { valor: '24h', etiqueta: '24 horas' },
  { valor: '3d', etiqueta: '3 días' },
  { valor: '7d', etiqueta: '7 días' },
  { valor: 'hasta_fecha', etiqueta: 'Hasta una fecha' },
  { valor: 'recurrente', etiqueta: 'Recurrente' },
  { valor: 'permanente', etiqueta: 'Permanente' },
];

/** El ajuste que se edita con casillas en vez de con un campo de texto. */
const CLAVE_TIPOS_PASE = 'allowed_pass_types';

export default function SystemConfig() {
  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modifiedConfigs, setModifiedConfigs] = useState({});

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const response = await api.get('/system-config');
      setConfigs(response.data.configs);
      setModifiedConfigs({});
    } catch (error) {
      console.error('Error fetching configs:', error);
      toast.error('Error al cargar las configuraciones');
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = (key, value, dataType) => {
    setModifiedConfigs(prev => ({
      ...prev,
      [key]: { value, dataType }
    }));
  };

  const handleToggle = (configKey, currentValue) => {
    handleConfigChange(configKey, !currentValue, 'boolean');
  };

  const handleInputChange = (configKey, value, dataType) => {
    handleConfigChange(configKey, value, dataType);
  };

  const saveChanges = async () => {
    setSaving(true);
    const errors = [];
    const successes = [];

    for (const [key, data] of Object.entries(modifiedConfigs)) {
      try {
        await api.patch(`/system-config/${key}`, { value: data.value });
        successes.push(key);
      } catch (error) {
        console.error(`Error updating ${key}:`, error);
        errors.push(key);
      }
    }

    if (successes.length > 0) {
      toast.success(
        `${successes.length} ${plural(successes.length, "configuración", "configuraciones")} ${plural(successes.length, "actualizada", "actualizadas")}`
      );
      await fetchConfigs(); // Recargar para obtener los datos actualizados
    }

    if (errors.length > 0) {
      toast.error(`Error al actualizar ${errors.length} configuraciones`);
    }

    setSaving(false);
  };

  const resetToDefaults = async () => {
    if (!window.confirm('¿Estás seguro de restablecer todas las configuraciones a sus valores predeterminados?')) {
      return;
    }

    try {
      await api.post('/system-config/reset-defaults');
      toast.success('Configuraciones restablecidas');
      await fetchConfigs();
    } catch (error) {
      console.error('Error resetting configs:', error);
      toast.error('Error al restablecer las configuraciones');
    }
  };

  const getConfigValue = (config) => {
    const key = config.key;
    if (modifiedConfigs[key]) {
      return modifiedConfigs[key].value;
    }
    return config.value;
  };

  /** El título del ajuste: la descripción en español, no la clave técnica. */
  const tituloDe = (config) => config.description || config.key;

  /**
   * Las casillas que se enseñan para `allowed_pass_types`: el catálogo
   * completo, más cualquier valor guardado que no esté en él.
   *
   * Lo segundo importa: si la base trae un valor que el catálogo ya no
   * tiene, omitirlo de la pantalla lo borraría en silencio al guardar, que es
   * exactamente el problema que estas casillas vienen a cerrar.
   */
  const opcionesDePase = (seleccionados) => {
    const conocidos = TIPOS_PASE.map((t) => t.valor);
    const extras = seleccionados
      .filter((v) => !conocidos.includes(v))
      .map((v) => ({ valor: v, etiqueta: v }));
    return [...TIPOS_PASE, ...extras];
  };

  /**
   * Marca o desmarca una duración de pase.
   *
   * Se guarda como lista (el ajuste es `dataType: 'json'` y el servidor
   * acepta el arreglo tal cual), en el orden del catálogo, para que la lista
   * se lea siempre igual sin importar en qué orden se tocaron las casillas.
   *
   * No se permite quedarse sin ninguna: el servidor valida el valor con
   * `notEmpty()`, así que una lista vacía se rechazaría con un 400 que en
   * pantalla sólo se lee como «error al actualizar», y además el residente
   * quedaría sin ninguna duración que elegir.
   */
  const alternarTipoPase = (config, valor) => {
    const actuales = Array.isArray(getConfigValue(config)) ? getConfigValue(config) : [];
    const estaba = actuales.includes(valor);

    if (estaba && actuales.length === 1) {
      toast.info('Debe quedar al menos una duración activa: el residente necesita poder elegir alguna.');
      return;
    }

    const siguiente = estaba
      ? actuales.filter((v) => v !== valor)
      : [...actuales, valor];

    const orden = opcionesDePase(siguiente).map((o) => o.valor);
    siguiente.sort((a, b) => orden.indexOf(a) - orden.indexOf(b));

    handleConfigChange(config.key, siguiente, 'json');
  };

  /** El interruptor de un ajuste de sí/no. */
  const renderInterruptor = (config) => {
    const encendido = Boolean(getConfigValue(config));

    return (
      <>
        <span className={`cfg__estado${encendido ? ' cfg__estado--activo' : ''}`}>
          {encendido ? 'Activado' : 'Desactivado'}
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={encendido}
          aria-label={tituloDe(config)}
          className="cfg__interruptor"
          onClick={() => handleToggle(config.key, encendido)}
        />
      </>
    );
  };

  /** Las casillas de `allowed_pass_types`. */
  const renderTiposPase = (config) => {
    const seleccionados = Array.isArray(getConfigValue(config)) ? getConfigValue(config) : [];
    const opciones = opcionesDePase(seleccionados);

    return (
      <div className="cfg__casillas" role="group" aria-label={tituloDe(config)}>
        {opciones.map((opcion) => {
          const marcada = seleccionados.includes(opcion.valor);
          return (
            <label
              key={opcion.valor}
              className={`cfg__casilla${marcada ? ' cfg__casilla--marcada' : ''}`}
            >
              <input
                type="checkbox"
                checked={marcada}
                onChange={() => alternarTipoPase(config, opcion.valor)}
              />
              <span className="cfg__casilla-texto">
                <span>{opcion.etiqueta}</span>
                <span className="cfg__casilla-valor">{opcion.valor}</span>
              </span>
            </label>
          );
        })}
      </div>
    );
  };

  const renderConfigControl = (config) => {
    const value = getConfigValue(config);

    // Lo que no se puede editar sigue siendo un control —se enfoca y el lector
    // de pantalla lo anuncia como de sólo lectura— pero se ve distinto de un
    // campo editable: candado al lado, borde punteado y la nota con el motivo.
    if (SOLO_LECTURA[config.key]) {
      return (
        <>
          <Lock size={14} className="cfg__candado" aria-hidden="true" />
          <input
            type="text"
            className="cfg__campo cfg__campo--solo-lectura"
            value={String(value ?? '')}
            readOnly
            aria-disabled="true"
            aria-label={`${tituloDe(config)} (sólo lectura)`}
          />
        </>
      );
    }

    if (config.dataType === 'boolean') {
      return renderInterruptor(config);
    }

    if (config.dataType === 'number') {
      return (
        <input
          type="number"
          className="cfg__campo"
          value={value}
          aria-label={tituloDe(config)}
          onChange={(e) => handleInputChange(config.key, e.target.value, 'number')}
        />
      );
    }

    /*
     * Una lista o un objeto NO se editan en un campo de texto. Es lo que pasaba
     * con `allowed_pass_types`: se pintaba como `unica,hoy,24h,…`, cortado a
     * media palabra, y al guardar se mandaba esa cadena a un ajuste que el
     * servidor espera como JSON. Hoy sólo ese ajuste es de tipo lista y tiene
     * sus casillas; si mañana aparece otro, se enseña sin poder romperlo y con
     * la clave a la vista para que soporte sepa de cuál se trata.
     */
    if (value !== null && typeof value === 'object') {
      return (
        <input
          type="text"
          className="cfg__campo cfg__campo--solo-lectura"
          value={JSON.stringify(value)}
          readOnly
          aria-disabled="true"
          aria-label={`${tituloDe(config)} (sólo lectura)`}
        />
      );
    }

    return (
      <input
        type="text"
        className="cfg__campo"
        value={String(value ?? '')}
        aria-label={tituloDe(config)}
        onChange={(e) => handleInputChange(config.key, e.target.value, 'string')}
      />
    );
  };

  if (loading) {
    return (
      <div className="cfg__cargando">
        <div className="animate-spin cfg__girador" />
      </div>
    );
  }

  const hasChanges = Object.keys(modifiedConfigs).length > 0;
  // Ver `GRUPOS_OCULTOS`: se oculta temporalmente, a solicitud de Rafael Martínez.
  const gruposVisibles = Object.entries(configs).filter(
    ([grupo]) => !GRUPOS_OCULTOS.includes(grupo)
  );

  return (
    <div className="cfg">
      <div className="cfg__encabezado">
        <h2 className="cfg__titulo">Configuración del Sistema</h2>
        <p className="cfg__subtitulo">
          Administra las configuraciones generales del sistema residencial
        </p>
      </div>

      {gruposVisibles.length === 0 ? (
        <Card>
          <div className="cfg__vacio">
            <Settings size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
            <p>No hay configuraciones disponibles</p>
          </div>
        </Card>
      ) : (
        <div className="cfg__grupos">
          {gruposVisibles.map(([grupo, ajustes]) => {
            const IconoGrupo = ICONOS_GRUPO[grupo]?.icono || Settings;
            const colorIcono = ICONOS_GRUPO[grupo]?.color || 'var(--primary)';

            return (
              <Card key={grupo}>
                <div className="cfg__grupo-encabezado">
                  <div
                    className="cfg__grupo-icono"
                    style={{ backgroundColor: `${colorIcono}20`, color: colorIcono }}
                  >
                    <IconoGrupo size={24} aria-hidden="true" />
                  </div>
                  <h3 className="cfg__grupo-titulo">{NOMBRES_GRUPO[grupo] || grupo}</h3>
                </div>

                <div className="cfg__ajustes">
                  {ajustes.map((config) => {
                    const esBloque = config.key === CLAVE_TIPOS_PASE;
                    const motivoSoloLectura = SOLO_LECTURA[config.key];

                    return (
                      <div
                        key={config.key}
                        className={`cfg__ajuste${esBloque ? ' cfg__ajuste--bloque' : ''}`}
                      >
                        <div className="cfg__info">
                          <p className="cfg__nombre">{tituloDe(config)}</p>

                          <p className="cfg__meta">
                            <code className="cfg__clave">{config.key}</code>

                            {config.updatedAt && (
                              <span className="cfg__fecha">
                                <Info size={10} aria-hidden="true" />
                                Actualizado {new Date(config.updatedAt).toLocaleDateString('es-SV')}
                              </span>
                            )}

                            {modifiedConfigs[config.key] && (
                              <span className="cfg__modificado">
                                <span className="cfg__punto" aria-hidden="true" />
                                Modificado
                              </span>
                            )}
                          </p>

                          {motivoSoloLectura && (
                            <p className="cfg__nota">
                              Sólo lectura · {motivoSoloLectura}
                            </p>
                          )}

                          {esBloque && (
                            <p className="cfg__nota">
                              Marque las duraciones que el residente podrá elegir al crear un pase.
                              Debe quedar al menos una.
                            </p>
                          )}
                        </div>

                        {esBloque ? (
                          renderTiposPase(config)
                        ) : (
                          <div className="cfg__control">{renderConfigControl(config)}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <div className="cfg__acciones">
        <Button
          variant="outline"
          onClick={resetToDefaults}
          leftIcon={RotateCcw}
        >
          Restablecer valores
        </Button>
        <Button
          variant="primary"
          onClick={saveChanges}
          loading={saving}
          disabled={!hasChanges || saving}
          leftIcon={Save}
        >
          Guardar cambios
        </Button>
      </div>
    </div>
  );
}
